// User service for managing all users
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../firebase';

class UserService {
  constructor() {
    this.collectionName = 'users';
  }

  // Create a new user
  async createUser(userData) {
    try {
      const userWithTimestamp = {
        ...userData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: null
      };
      
      const docRef = await addDoc(collection(db, this.collectionName), userWithTimestamp);
      
      return { id: docRef.id, ...userWithTimestamp };
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Get all users
  async getAllUsers() {
    try {
      const q = query(collection(db, this.collectionName));
      const querySnapshot = await getDocs(q);
      const users = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id,
          ...data
        });
      });

      // Sort by name (handle cases where name might be undefined)
      users.sort((a, b) => {
        const nameA = a.name || a.email || '';
        const nameB = b.name || b.email || '';
        return nameA.localeCompare(nameB);
      });

      return users;
    } catch (error) {
      console.error('Error getting users:', error);
      return [];
    }
  }

  // Get user by ID
  async getUserById(userId) {
    try {
      const userRef = doc(db, this.collectionName, userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return null;
      }

      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } catch (error) {
      console.error('Error getting user by ID:', error);
      return null;
    }
  }

  // Update a user
  async updateUser(userId, userData) {
    try {
      const updateData = {
        ...userData,
        updatedAt: new Date().toISOString()
      };

      const userRef = doc(db, this.collectionName, userId);
      await updateDoc(userRef, updateData);

      return { id: userId, ...updateData };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete a user
  async deleteUser(userId) {
    try {
      const userRef = doc(db, this.collectionName, userId);
      await deleteDoc(userRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Get users by role
  async getUsersByRole(role) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('role', '==', role)
      );

      const querySnapshot = await getDocs(q);
      const users = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id,
          ...data
        });
      });

      return users;
    } catch (error) {
      console.error('Error getting users by role:', error);
      return [];
    }
  }

  // Get users by status
  async getUsersByStatus(status) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('status', '==', status)
      );

      const querySnapshot = await getDocs(q);
      const users = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id,
          ...data
        });
      });

      return users;
    } catch (error) {
      console.error('Error getting users by status:', error);
      return [];
    }
  }

  // Search users
  async searchUsers(searchTerm) {
    try {
      const q = query(collection(db, this.collectionName));
      const querySnapshot = await getDocs(q);
      const users = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const searchableText = `${data.name} ${data.email}`.toLowerCase();
        
        if (searchableText.includes(searchTerm.toLowerCase())) {
          users.push({
            id: doc.id,
            ...data
          });
        }
      });

      return users;
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }

  // Reset user password (placeholder - would integrate with Firebase Auth)
  async resetPassword(userId) {
    try {
      // In a real implementation, this would:
      // 1. Generate a reset token
      // 2. Send an email with reset instructions
      // 3. Or use Firebase Auth's sendPasswordResetEmail
      
      // For now, we'll just simulate success
      console.log(`Password reset requested for user: ${userId}`);
      
      // You would implement actual password reset logic here
      // For example:
      // const user = await this.getUserById(userId);
      // if (user && user.email) {
      //   await sendPasswordResetEmail(auth, user.email);
      // }
      
      return true;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  // Update user last login
  async updateLastLogin(userId) {
    try {
      const updateData = {
        lastLoginAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const userRef = doc(db, this.collectionName, userId);
      await updateDoc(userRef, updateData);

      return true;
    } catch (error) {
      console.error('Error updating last login:', error);
      throw error;
    }
  }
}

const userService = new UserService();
export default userService;
